import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { t } from 'i18next';
import { Effect } from '@redux-saga/types';
import {
    ManualAssistantErrorResponse,
    ManualAssistantQueryRequest,
    ManualAssistantQueryResponse,
    queryManualAssistant,
} from 'api/manualAssistant';
import {
    queryManualAssistantFailure,
    queryManualAssistantSuccess,
} from 'redux/actions/manualAssistantActions';
import { getUserSelector } from 'redux/selectors/userSelector';
import { ManualAssistantQueryResult } from 'redux/types/manualAssistantActionModel';
import { getLocalStorageValue } from 'shared/service/CommonService';
import { assertManualAssistantQueryAllowed, LocalStorageKey } from 'shared/utils/Constants';
import * as actionTypes from '../actionTypes';

const isErrorResponse = (data: ManualAssistantQueryResponse): data is ManualAssistantErrorResponse => {
    return (data as ManualAssistantErrorResponse).error !== undefined;
};

export const mapQueryResponse = (data: ManualAssistantQueryResponse): ManualAssistantQueryResult => {
    if (isErrorResponse(data)) {
        if (data.error === 'not_found') {
            return { kind: 'not_found', message: data.message };
        }
        if (data.error.indexOf('guardrail_') === 0) {
            return { kind: 'refusal', error: data.error, message: data.message };
        }
        return { kind: 'failed', error: data.error, message: t('manual_assistant_query_failed') };
    }
    if (data.steps && data.citation && data.provider_used) {
        return {
            kind: 'grounded',
            steps: data.steps,
            citation: data.citation,
            provider_used: data.provider_used,
        };
    }
    return { kind: 'failed', error: 'invalid_response', message: t('manual_assistant_query_failed') };
};

function* queryManualAssistantSaga(action: Effect) {
    const payload = action.payload as ManualAssistantQueryRequest;
    const user: { id?: string; roleID?: number } | undefined = yield select(getUserSelector);
    const token = getLocalStorageValue(LocalStorageKey.ACCESS_TOKEN);

    if (!assertManualAssistantQueryAllowed(token, user ? user.roleID : undefined)) {
        yield put(
            queryManualAssistantFailure({
                error: 'forbidden',
                message: t('manual_assistant_query_forbidden'),
            }),
        );
        return;
    }

    if (!payload.user_query || payload.user_query.trim() === '') {
        yield put(
            queryManualAssistantFailure({
                error: 'validation_error',
                message: t('manual_assistant_empty_question'),
            }),
        );
        return;
    }

    try {
        const data: ManualAssistantQueryResponse = yield call(queryManualAssistant, payload);
        yield put(queryManualAssistantSuccess(mapQueryResponse(data)));
    } catch (e) {
        void e;
        yield put(
            queryManualAssistantFailure({
                error: 'query_failed',
                message: t('manual_assistant_query_failed'),
            }),
        );
    }
}

function* manualAssistantSaga() {
    yield all([takeLatest(actionTypes.MANUAL_ASSISTANT_QUERY_REQUEST, queryManualAssistantSaga)]);
}

export default manualAssistantSaga;
